import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Button } from '@/components/ui/button';
import { Upload, X, Plus, AlertCircle } from 'lucide-react';

interface UploadedFile {
  id: string;
  name: string;
  type: 'pdf' | 'image';
}

const AdminResolutionWorkflowPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [disputeType, setDisputeType] = useState('customer');
  const [resolutionStatement, setResolutionStatement] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [verdict, setVerdict] = useState('uphold');
  const [resolutionAmount, setResolutionAmount] = useState('');
  const [notifyLegal, setNotifyLegal] = useState(false);
  const [caseData, setCaseData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiService.request(`/admin/resolutions/${caseId}`, 'GET');
        if (response.success && response.data) {
          const data = response.data;
          setCaseData(data);
          if (data.dispute_type) setDisputeType(data.dispute_type);
          if (data.resolution_amount) setResolutionAmount(String(data.resolution_amount));
          if (data.uploaded_files && Array.isArray(data.uploaded_files)) {
            setUploadedFiles(data.uploaded_files.map((f: any) => ({
              id: f.id,
              name: f.filename || f.name,
              type: f.type || 'pdf'
            })));
          }
          if (data.verdict) setVerdict(data.verdict);
        }
      } catch (error) {
        console.error('Error fetching case data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (caseId) {
      fetchData();
    }
  }, [caseId]);

  const handleFileRemove = (id: string) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== id));
  };

  const disputeClassifications = [
    { id: 'customer', label: 'Customer', icon: '👤' },
    { id: 'provider', label: 'Provider', icon: '🏢' },
    { id: 'platform', label: 'Platform', icon: '⚙️' }
  ];

  return (
    <>
      <Helmet>
        <title>Resolution Workflow - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          {/* Breadcrumb */}
          <div className="text-sm text-gray-600 mb-8">
            <span className="hover:text-gray-900 cursor-pointer">Operations</span>
            <span className="mx-2">›</span>
            <span className="hover:text-gray-900 cursor-pointer">Dashboard</span>
            <span className="mx-2">›</span>
            <span className="font-semibold text-gray-900">CASE #{caseId}</span>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Resolution Workflow</h1>
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 font-bold text-xs uppercase tracking-wide">
                ● {caseData?.status || 'Under Review'}
              </span>
              <span className="text-gray-600 font-medium">Assigned to: {caseData?.assigned_to || '—'}</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="col-span-2 space-y-8">
              {/* Dispute Classification */}
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-6">Dispute Classification</h3>
                <div className="grid grid-cols-3 gap-4">
                  {disputeClassifications.map((classification) => (
                    <button
                      key={classification.id}
                      onClick={() => setDisputeType(classification.id)}
                      className={`px-4 py-6 rounded-xl border-2 transition-all font-semibold text-center ${
                        disputeType === classification.id
                          ? 'border-teal-600 bg-teal-50 text-teal-900'
                          : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-2">{classification.icon}</div>
                      <div className="text-sm font-bold">{classification.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Related Transaction */}
              {caseData?.transaction_id && (
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-4">Related Transaction Record</h3>
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <span className="text-lg">📋</span>
                  <span className="font-mono font-bold text-gray-900">{caseData?.transaction_id || '—'}</span>
                  <span className="text-gray-400">|</span>
                  <span className="font-bold text-gray-900">{caseData?.transaction_amount ? `£${Number(caseData.transaction_amount).toFixed(2)}` : '—'}</span>
                </div>
              </div>
              )}

              {/* Internal & External Communication */}
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-6">Internal & External Communication</h3>
                
                <div className="mb-6">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-3">Resolution Statement</label>
                  <textarea
                    value={resolutionStatement}
                    onChange={(e) => setResolutionStatement(e.target.value)}
                    placeholder="Draft the final communication to be sent to both parties upon submission..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 resize-none"
                    rows={6}
                  />
                </div>

                <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                  <input
                    type="checkbox"
                    id="legal-notify"
                    checked={notifyLegal}
                    onChange={(e) => setNotifyLegal(e.target.checked)}
                    className="w-4 h-4 accent-teal-600 rounded cursor-pointer"
                  />
                  <label htmlFor="legal-notify" className="text-sm text-gray-700 cursor-pointer">
                    Automatically notify legal counsel of this resolution
                  </label>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="col-span-1 space-y-8">
              {/* Evidence Dossier */}
              <div className="bg-white rounded-xl border border-gray-200 p-8">
                <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-6">Evidence Dossier</h3>
                
                {/* File Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50 mb-6">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-gray-900 mb-1">Drag files to upload</p>
                  <p className="text-xs text-gray-600">PDF, PNG, JPG (Max 50MB per file)</p>
                </div>

                {/* Uploaded Files */}
                <div className="space-y-3">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">
                          {file.type === 'pdf' ? '📄' : '🖼️'}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{file.name}</span>
                      </div>
                      <button
                        onClick={() => handleFileRemove(file.id)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Arbiter's Final Verdict */}
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl p-8 text-white shadow-lg">
                <h3 className="text-xs font-bold uppercase tracking-widest mb-6 opacity-90">Arbiter's Final Verdict</h3>
                
                {/* Verdict Options */}
                <div className="space-y-4 mb-8">
                  {[
                    { id: 'uphold', label: 'Uphold Dispute' },
                    { id: 'dismiss', label: 'Dismiss Case' },
                    { id: 'partial', label: 'Partial Grant' }
                  ].map((option) => (
                    <label key={option.id} className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="radio"
                        name="verdict"
                        value={option.id}
                        checked={verdict === option.id}
                        onChange={(e) => setVerdict(e.target.value)}
                        className="w-4 h-4 accent-white"
                      />
                      <span className="text-sm font-medium group-hover:opacity-80">{option.label}</span>
                    </label>
                  ))}
                </div>

                {/* Divider */}
                <div className="border-t border-teal-500 opacity-50 my-6"></div>

                {/* Resolution Amount */}
                <div className="mb-6">
                  <p className="text-xs font-bold uppercase tracking-wider mb-3 opacity-90">Resolution Amount (GBP)</p>
                  <div className="bg-teal-800 rounded-lg p-4">
                    <input
                      type="text"
                      value={`$ ${resolutionAmount}`}
                      onChange={(e) => setResolutionAmount(e.target.value.replace('$ ', ''))}
                      className="w-full bg-transparent text-3xl font-bold text-white border-none outline-none"
                    />
                  </div>
                  <p className="text-xs text-teal-100 mt-3">* Calculated based on 50% partial grant policy</p>
                </div>

                {/* Commit Button */}
                <Button className="w-full bg-white text-teal-700 hover:bg-gray-100 font-bold text-sm py-3 rounded-lg flex items-center justify-center gap-2 shadow-lg">
                  <span>Commit Resolution</span>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-8 border-t border-gray-200 flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs font-bold text-gray-600 uppercase">Date Initiated</p>
              <p className="text-sm font-semibold text-gray-900">{caseData?.date_initiated ? new Date(caseData.date_initiated).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) + ' - ' + new Date(caseData.date_initiated).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' AM' : '—'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-bold text-gray-600 uppercase">Risk Factor</p>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${caseData?.risk_level === 'high' ? 'bg-red-600' : caseData?.risk_level === 'medium' ? 'bg-yellow-600' : 'bg-green-600'}`}></div>
                <p className={`text-sm font-semibold ${caseData?.risk_level === 'high' ? 'text-red-600' : caseData?.risk_level === 'medium' ? 'text-yellow-600' : 'text-green-600'}`}>{caseData?.risk_description || 'High Volatility'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button className="border-2 border-gray-300 bg-white text-gray-900 hover:bg-gray-50 font-bold text-sm px-6 py-2">
                Save as Draft
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white font-bold text-sm px-6 py-2">
                Discard Case
              </Button>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminResolutionWorkflowPage;
